/**
 * Reactive media-query hooks.
 *
 * useMediaQuery(query, fallback?) -> boolean, updates on viewport change.
 * useDeviceClass()                -> "mobile" | "tablet" | "desktop".
 * usePrefersReducedMotion()       -> boolean.
 *
 * SSR-safe (returns the fallback until mounted) and listener-cleaned.
 * Breakpoints mirror DESIGN-GUIDELINES section 7: tablet 768, desktop 1024.
 */

import { useEffect, useState } from "react";

export type DeviceClass = "mobile" | "tablet" | "desktop";

export function useMediaQuery(query: string, fallback = false): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return fallback;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handler = () => setMatches(mql.matches);
    handler();
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

/**
 * Resolves the current device class so a page can render a layout that feels
 * native to the device (true desktop-first vs. mobile-first), not just a
 * reflowed grid. Defaults to "desktop" before mount to avoid a mobile flash
 * on wide screens.
 */
export function useDeviceClass(): DeviceClass {
  const isDesktop = useMediaQuery("(min-width: 1024px)", true);
  const isTablet = useMediaQuery("(min-width: 768px)", true);
  if (isDesktop) return "desktop";
  if (isTablet) return "tablet";
  return "mobile";
}

export function usePrefersReducedMotion(): boolean {
  return useMediaQuery("(prefers-reduced-motion: reduce)", false);
}
